import * as React from 'react';
import styled from 'styled-components';
import { useState } from 'react';
import {
  HorizontalLayout,
  Section,
} from '@/features/new-landing-page/components/section';
import { GradientGlowContainer } from '@/features/new-landing-page/components/gradient-glow-container';
import { StrapiTextual } from '@/features/strapi/components/textual';
import { MainBannerContent } from '@/features/new-landing-page/pages/homepage-content';
import { BuiltForShopify } from '@/features/new-landing-page/components/built-for-shopify';
import { HeroVideo } from '@/features/new-landing-page/components/video';
import { StrapiImageTextual } from '@/features/strapi/components/image-textual';
import { VSpace } from '@/components/spacing';
import { CustomersScroller } from '@/features/new-landing-page/components/customers-scroller';
import { validateEmail } from '@/features/details/utils';
import { BookDemoOverlay } from '@/features/new-landing-page/components/book-demo-overlay';
import { breakpoints } from '@/components/responsive';

export function MainBannerV2Section() {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState(``);
  const [isBookShown, setIsBookShown] = useState(false);
  const [email, setEmail] = useState(``);

  const onClick = () => {
    if (inputRef?.current?.value) {
      if (validateEmail(inputRef?.current?.value)) {
        setIsBookShown(true);
        setErrorMessage(``);
        setEmail(inputRef?.current?.value);
      } else {
        setErrorMessage(`Please enter a valid business email address`);
      }
    }
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === `Enter`) {
      onClick();
    }
  };

  return (
    <Section>
      {isBookShown && email && (
        <BookDemoOverlay email={email} onClose={() => setIsBookShown(false)} />
      )}
      <GradientGlowContainer />
      <VSpace className="only-desktop" value={2} />
      <TwoColumns>
        <LeftColumn>
          <StrapiTextual {...MainBannerContent.titleV2} />
          <StrapiTextual {...MainBannerContent.subtitleV2} />
          <BuiltForShopify />
          <InputSection>
            <DemoEmailInputWrapper>
              <DemoEmailInput
                ref={inputRef}
                placeholder="Your business email*"
                onKeyDown={onInputKeyDown}
              />
              <DemoEmailButton onClick={onClick}>Get a demo</DemoEmailButton>
            </DemoEmailInputWrapper>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <HorizontalLayout gap="2rem" className="benefits">
              {MainBannerContent.benefitUsps.map((usp) => (
                <StrapiImageTextual
                  key={usp.textual.text}
                  {...usp}
                  gap="0.3rem"
                />
              ))}
            </HorizontalLayout>
          </InputSection>
        </LeftColumn>
        <RightColumn>
          <HeroVideo />
        </RightColumn>
      </TwoColumns>
      <VSpace className="only-desktop" value={2} />
      <VSpace className="only-mobile" value={2.5} />
      <StrapiTextual {...MainBannerContent.trustedBy} />
      <VSpace className="only-mobile" value={0.5} />
      <CustomersScroller />
      <VSpace value={1} />
    </Section>
  );
}

const TwoColumns = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 55vw;

  gap: 2rem;

  .hero-video-wrapper {
    width: 48vw;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-right: 0 !important;
    top: -3rem;
    left: -4rem;
  }

  && *:not(input) {
    user-select: none;
  }

  ${breakpoints.down(`md`)} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .hero-video-wrapper {
      width: 85vw;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      margin-right: 0vw;
      top: 0rem;
      left: 0rem;
    }
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-left: 8vw;
  gap: 2rem;

  margin-top: -2rem;
  margin-left: -2rem;

  .strapi-textual {
    text-align: start;
  }

  .built-for-shopify {
    align-self: flex-start;
  }

  ${breakpoints.down(`md`)} {
    padding: 3vw;
    width: 100%;
    justify-content: center;
    align-items: center;

    margin-top: 0;
    margin-left: 0;

    .strapi-textual {
      text-align: center;
    }

    .built-for-shopify {
      align-self: center;
    }

    .benefits {
      margin-left: -1rem;
    }
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const InputSection = styled.div`
  margin-top: 1rem;
  max-width: 80%;
  min-width: 80%;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  ${breakpoints.down(`md`)} {
    max-width: 90%;
    min-width: 90%;
  }
`;

const DemoEmailInputWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const DemoEmailInput = styled.input`
  font-size: 1.7rem;
  border-radius: 3rem;
  border: solid 1px #000;
  background-color: #fff;
  padding: 1.5rem 15rem 1.5rem 2.5rem;
  font-family: Eesti, sans-serif;
  width: 100%;
  font-weight: 300;

  &&::placeholder {
    color: #63707c;
  }

  &&:focus {
    border: solid 1px #000;
    outline: none;
  }
`;

const ErrorMessage = styled.p`
  color: #dc4949;
  font-size: 1.4rem;
  margin: -1rem 0 0 2.7rem;
`;

const DemoEmailButton = styled.button`
  position: absolute;
  right: 0rem;
  height: 100%;
  width: fit-content;
  appearance: none;
  border: none;
  outline: none;

  font-family: Eesti, sans-serif;
  font-size: 1.7rem;
  background-color: #dc4949;
  box-shadow: 0px 0px 4px rgba(220, 73, 73, 0.2);
  color: white;

  padding: 1.5rem 2.5rem;
  border-radius: 3rem;

  cursor: pointer;

  transition: transform 0.2s ease-in-out;

  &&:hover {
    transform: scale(1.05);
  }

  &&:active {
    transform: scale(1);
  }
`;
